import { isEmail } from '@mantine/form';
import { HumanContact, OrganizationContact } from './types';

/**
 * Validates the email addresses on a list of contacts (clients or adverse
 * parties). Returns an error message string if any email is malformed, or
 * `undefined` if all emails are valid (or empty).
 *
 * Each email is `.trim()`-ed before validation so a value like
 * `"user@example.com "` is treated as valid — the form's `transformValues`
 * (PRA-191) strips the whitespace before the trimmed value reaches Firestore.
 * Without the trim here, `isEmail()` would reject the value and block submit.
 */
export const validateContacts = (
  contacts: (HumanContact | OrganizationContact)[],
): string | undefined => {
  for (const contact of contacts) {
    const contactName =
      contact.type === 'human'
        ? `${contact.firstName} ${contact.lastName}`
        : contact.companyName;

    for (const emailAddress of contact.emailAddresses) {
      const trimmedValue = emailAddress.value.trim();
      if (trimmedValue === '') {
        // We currently allow empty email addresses. No good reason.
        continue;
      }
      // Mantine's validation functions are a little weird.
      // The `isEmail` function accepts an error message and returns a *new*
      // function that accepts the form value.
      const emailError = isEmail()(trimmedValue);
      if (emailError) {
        return `${contactName} has an invalid email address: ${emailAddress.value}`;
      }
    }
  }
  return undefined;
};
